/**
 * P1-005 Resource Intelligence Dashboard - Capacity Intelligence Layer
 * 
 * The UI that answers: "Do we have enough capacity to deliver?"
 * 
 * Three-View Dashboard for Capacity Intelligence:
 * 1. Executive View - Do we have capacity? Where are bottlenecks? What commitments are at risk?
 * 2. PMO View - Demand vs Capacity, Team Utilization, Forecasts, Heatmaps
 * 3. Resource Manager View - Who is overloaded? Underutilized? Skills gaps?
 * 
 * Architecture Position: Intent → Decision → Governance → Delivery → Outcome → RESOURCE INTELLIGENCE
 * 
 * Design Philosophy:
 *   "The primary entity is not the person.
 *    The primary entity is: Capacity, Availability, Utilization, Constraint.
 *    The resource simply owns the capacity."
 * 
 * Executive Questions Answered:
 * - Do we have enough capacity?
 * - Where are the bottlenecks?
 * - What is likely to become constrained?
 * - Which commitments are at risk?
 * - What should be delayed?
 * - What should be accelerated?
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, AlertTriangle, CheckCircle, Clock, TrendingUp,
  TrendingDown, Loader2, Activity, Zap, Gauge, Target,
  BarChart3, PieChart as PieChartIcon, RefreshCw, AlertCircle,
  Cpu, UserCheck, UserX, Layers, Building2, Briefcase,
  Calendar, ChevronRight, Filter, Plus, ArrowUpRight,
  ArrowDownRight, CircleDot, Thermometer, Brain, Shield,
  Lightbulb, XCircle
} from 'lucide-react';
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  Treemap
} from 'recharts';
import axios from 'axios';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';
const getAuthHeaders = () => {
  const token = localStorage.getItem('simpliview_token');
  const tenantId =
    localStorage.getItem('impersonated_tenant_id') ||
    localStorage.getItem('simpliview_user_tenant_id') ||
    localStorage.getItem('simpliview_tenant_id');
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(tenantId ? { 'X-Tenant-ID': tenantId } : {})
  };
};

// =============================================================================
// FORMATTING HELPERS
// =============================================================================

const formatPercent = (value) => {
  if (value === undefined || value === null) return '-';
  return `${value.toFixed(1)}%`;
};

const formatHours = (value) => {
  if (value === undefined || value === null) return '-';
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K hrs`;
  return `${value.toFixed(0)} hrs`;
};

// =============================================================================
// CAPACITY RISK SCORE CALCULATION
// =============================================================================

const calculateCapacityRiskScore = (dashboard) => {
  if (!dashboard?.summary) return { score: 'LOW', color: 'emerald', value: 0 };
  
  const { summary, bottlenecks } = dashboard;
  let riskValue = 0;
  
  // Factor 1: Overallocation (40% weight)
  const overallocatedPercent = (summary.resources_overallocated / Math.max(summary.total_resources, 1)) * 100;
  if (overallocatedPercent > 20) riskValue += 40;
  else if (overallocatedPercent > 10) riskValue += 25;
  else if (overallocatedPercent > 5) riskValue += 15;
  else if (overallocatedPercent > 0) riskValue += 5;
  
  // Factor 2: Critical dependencies (25% weight)
  const criticalDeps = bottlenecks?.critical_dependencies || 0;
  if (criticalDeps > 5) riskValue += 25;
  else if (criticalDeps > 2) riskValue += 15;
  else if (criticalDeps > 0) riskValue += 8;
  
  // Factor 3: Utilization health (20% weight)
  const utilization = summary.overall_utilized_percent || 0;
  if (utilization > 100) riskValue += 20;
  else if (utilization > 90) riskValue += 12;
  else if (utilization < 30) riskValue += 8; // Underutilization is also a risk
  
  // Factor 4: Critical resources at risk (15% weight)
  const criticalAtRisk = summary.critical_resources_at_risk || 0;
  if (criticalAtRisk > 3) riskValue += 15;
  else if (criticalAtRisk > 1) riskValue += 10;
  else if (criticalAtRisk > 0) riskValue += 5;
  
  // Determine risk level
  if (riskValue >= 70) return { score: 'CRITICAL', color: 'red', value: riskValue };
  if (riskValue >= 45) return { score: 'HIGH', color: 'orange', value: riskValue };
  if (riskValue >= 25) return { score: 'MEDIUM', color: 'amber', value: riskValue };
  return { score: 'LOW', color: 'emerald', value: riskValue };
};

// =============================================================================
// SHARED COMPONENTS
// =============================================================================

const MetricCard = ({ title, value, subtitle, icon: Icon, trend, color = 'blue', testId, onClick }) => {
  const colorMap = {
    blue: { bg: 'bg-blue-50', icon: 'text-blue-600', border: 'border-blue-100' },
    green: { bg: 'bg-emerald-50', icon: 'text-emerald-600', border: 'border-emerald-100' },
    emerald: { bg: 'bg-emerald-50', icon: 'text-emerald-600', border: 'border-emerald-100' },
    amber: { bg: 'bg-amber-50', icon: 'text-amber-600', border: 'border-amber-100' },
    orange: { bg: 'bg-orange-50', icon: 'text-orange-600', border: 'border-orange-100' },
    red: { bg: 'bg-red-50', icon: 'text-red-600', border: 'border-red-100' },
    purple: { bg: 'bg-purple-50', icon: 'text-purple-600', border: 'border-purple-100' },
    slate: { bg: 'bg-slate-50', icon: 'text-slate-600', border: 'border-slate-100' },
    indigo: { bg: 'bg-indigo-50', icon: 'text-indigo-600', border: 'border-indigo-100' }
  };
  const c = colorMap[color] || colorMap.blue;

  return (
    <div 
      className={`bg-white rounded-xl border ${c.border} p-5 shadow-sm hover:shadow-md transition-shadow ${onClick ? 'cursor-pointer' : ''}`}
      data-testid={testId}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-slate-500">{title}</p>
          <div className="flex items-baseline gap-2 mt-1">
            <span className="text-2xl font-bold text-slate-900">{value}</span>
            {trend !== undefined && (
              <span className={`text-sm font-medium flex items-center gap-0.5 ${
                trend > 0 ? 'text-emerald-600' : trend < 0 ? 'text-red-600' : 'text-slate-500'
              }`}>
                {trend > 0 ? <TrendingUp className="w-3.5 h-3.5" /> : trend < 0 ? <TrendingDown className="w-3.5 h-3.5" /> : null}
                {trend > 0 ? '+' : ''}{trend}%
              </span>
            )}
          </div>
          {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-xl ${c.bg}`}>
          <Icon className={`w-5 h-5 ${c.icon}`} />
        </div>
      </div>
    </div>
  );
};

const CapacityGauge = ({ value, label, size = 'medium' }) => {
  const percentage = Math.min(value || 0, 150);
  const sizeMap = {
    small: { container: 'w-16 h-16', text: 'text-sm' },
    medium: { container: 'w-24 h-24', text: 'text-lg' },
    large: { container: 'w-32 h-32', text: 'text-2xl' }
  };
  const s = sizeMap[size] || sizeMap.medium;
  
  const getColor = (val) => {
    if (val > 100) return '#ef4444';  // Red - Overallocated
    if (val > 80) return '#f59e0b';   // Amber - Near capacity
    if (val > 70) return '#f59e0b';   // Amber - At risk
    return '#10b981';                  // Green - Available
  };

  return (
    <div className="flex flex-col items-center">
      <div className={`relative ${s.container}`}>
        <svg className="transform -rotate-90" viewBox="0 0 36 36">
          <path
            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
            fill="none"
            stroke="#e5e7eb"
            strokeWidth="3"
          />
          <path
            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
            fill="none"
            stroke={getColor(value)}
            strokeWidth="3"
            strokeDasharray={`${Math.min(percentage, 100)}, 100`}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`${s.text} font-bold`} style={{ color: getColor(value) }}>
            {(value || 0).toFixed(0)}%
          </span>
        </div>
      </div>
      {label && <p className="text-sm font-medium text-slate-600 mt-2 text-center">{label}</p>}
    </div>
  );
};

const AllocationStatusBadge = ({ status }) => {
  const statusConfig = {
    available: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Available', icon: CheckCircle },
    near_capacity: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Near Capacity', icon: AlertTriangle },
    at_capacity: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'At Capacity', icon: AlertCircle },
    overallocated: { bg: 'bg-red-100', text: 'text-red-700', label: 'Overallocated', icon: XCircle }
  };
  
  const config = statusConfig[status] || statusConfig.available;
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
};

const CriticalityBadge = ({ criticality }) => {
  const config = {
    low: { bg: 'bg-slate-100', text: 'text-slate-700', label: 'Low' },
    medium: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Medium' },
    high: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'High' },
    mission_critical: { bg: 'bg-red-100', text: 'text-red-700', label: 'Mission Critical' }
  }[criticality] || { bg: 'bg-slate-100', text: 'text-slate-700', label: criticality };
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
};

const ResourceTypeBadge = ({ type }) => {
  const config = {
    human: { bg: 'bg-blue-100', text: 'text-blue-700', icon: Users },
    contractor: { bg: 'bg-purple-100', text: 'text-purple-700', icon: UserCheck },
    vendor: { bg: 'bg-amber-100', text: 'text-amber-700', icon: Building2 },
    shared_service: { bg: 'bg-indigo-100', text: 'text-indigo-700', icon: Layers },
    agent: { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: Cpu }
  }[type] || { bg: 'bg-slate-100', text: 'text-slate-700', icon: Users };
  
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}>
      <Icon className="w-3 h-3" />
      {type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
    </span>
  );
};

const CapacityRiskScoreCard = ({ riskScore }) => {
  const colorMap = {
    emerald: { bg: 'from-emerald-500 to-teal-600', text: 'text-emerald-300' },
    amber: { bg: 'from-amber-500 to-orange-600', text: 'text-amber-300' },
    orange: { bg: 'from-orange-500 to-red-500', text: 'text-orange-300' },
    red: { bg: 'from-red-600 to-red-800', text: 'text-red-300' }
  };
  const c = colorMap[riskScore.color] || colorMap.emerald;
  
  const interpretations = {
    LOW: 'Capacity is healthy. Delivery commitments are on track.',
    MEDIUM: 'Some constraints emerging. Monitor closely.',
    HIGH: 'Significant capacity constraints. Action required.',
    CRITICAL: 'Critical capacity shortage. Immediate intervention needed.'
  };
  
  return (
    <div className={`bg-gradient-to-r ${c.bg} rounded-2xl p-6 text-white`} data-testid="capacity-risk-score">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-white/80 mb-1">Capacity Risk Score</h3>
          <div className="flex items-baseline gap-3">
            <span className="text-4xl font-bold">{riskScore.score}</span>
            <span className={`text-sm px-3 py-1 rounded-full bg-white/20 ${c.text}`}>
              Risk Value: {riskScore.value}
            </span>
          </div>
          <p className="text-white/70 mt-2 text-sm max-w-md">
            {interpretations[riskScore.score]}
          </p>
        </div>
        <div className="p-4 bg-white/10 rounded-xl">
          <Thermometer className="w-10 h-10 text-white" />
        </div>
      </div>
    </div>
  );
};

const ViewTab = ({ active, label, icon: Icon, count, onClick, testId }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
      active 
        ? 'bg-slate-900 text-white shadow-lg' 
        : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
    }`}
    data-testid={testId || `tab-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <Icon className="w-5 h-5" />
    <span>{label}</span>
    {count !== undefined && (
      <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-bold ${
        active ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'
      }`}>
        {count}
      </span>
    )}
  </button>
);

// =============================================================================
// CAPACITY HEATMAP COMPONENT
// =============================================================================

const CapacityHeatmap = ({ data, groupBy = 'team' }) => {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-400">
        <div className="text-center">
          <BarChart3 className="w-10 h-10 mx-auto mb-2" />
          <p>No capacity data available</p>
        </div>
      </div>
    );
  }
  
  const getHeatmapColor = (status, allocatedPercent) => {
    if (status === 'overallocated' || allocatedPercent > 100) return '#ef4444'; // Red
    if (status === 'near_capacity' || allocatedPercent > 80) return '#f59e0b'; // Amber
    return '#10b981'; // Green
  };
  
  const getBgColor = (status, allocatedPercent) => {
    if (status === 'overallocated' || allocatedPercent > 100) return 'bg-red-50 border-red-200';
    if (status === 'near_capacity' || allocatedPercent > 80) return 'bg-amber-50 border-amber-200';
    return 'bg-emerald-50 border-emerald-200';
  };
  
  return (
    <div className="space-y-3" data-testid="capacity-heatmap">
      {data.map((item, idx) => {
        const allocatedPercent = item.allocated_percent || 0;
        const bgColor = getBgColor(item.status, allocatedPercent);
        const barColor = getHeatmapColor(item.status, allocatedPercent);
        
        return (
          <div 
            key={item.id || idx}
            className={`p-4 rounded-lg border ${bgColor} hover:shadow-md transition-shadow`}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <div 
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: barColor }}
                />
                <span className="font-medium text-slate-900">
                  {item.name || 'Unassigned'}
                </span>
                <span className="text-xs text-slate-500">
                  {item.resource_count} resources
                </span>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <span className="text-slate-600">
                  {formatHours(item.total_capacity_hours)} capacity
                </span>
                <span className="font-semibold" style={{ color: barColor }}>
                  {allocatedPercent.toFixed(0)}% allocated
                </span>
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
              <div 
                className="h-full rounded-full transition-all duration-500"
                style={{ 
                  width: `${Math.min(allocatedPercent, 100)}%`,
                  backgroundColor: barColor
                }}
              />
            </div>
            
            <div className="flex justify-between mt-2 text-xs text-slate-500">
              <span>{formatHours(item.total_allocated_hours)} allocated</span>
              <span>{formatHours(item.available_hours)} available</span>
              {item.overallocated_count > 0 && (
                <span className="text-red-600 font-medium">
                  {item.overallocated_count} overallocated
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// =============================================================================
// EXECUTIVE VIEW - Do we have capacity?
// =============================================================================

const ExecutiveView = ({ dashboard, heatmap, bottlenecks, forecast, onViewResource }) => {
  const { summary } = dashboard || {};
  
  // Use backend risk score from forecast if available, otherwise calculate locally
  const riskScore = forecast?.capacity_risk ? {
    score: forecast.capacity_risk.level,
    color: forecast.capacity_risk.level === 'CRITICAL' ? 'red' :
           forecast.capacity_risk.level === 'HIGH' ? 'orange' :
           forecast.capacity_risk.level === 'MEDIUM' ? 'amber' : 'emerald',
    value: forecast.capacity_risk.score
  } : calculateCapacityRiskScore(dashboard);
  
  const COLORS = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ec4899'];
  
  // Prepare type distribution data
  const typeData = Object.entries(summary?.by_type || {}).map(([key, value], idx) => ({
    name: key.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
    value: value.count || 0,
    capacity: value.capacity || 0,
    allocated: value.allocated || 0,
    color: COLORS[idx % COLORS.length]
  })).filter(d => d.value > 0);
  
  return (
    <div className="space-y-6" data-testid="executive-view">
      {/* Capacity Risk Score Hero */}
      <CapacityRiskScoreCard riskScore={riskScore} />
      
      {/* Today: Key Capacity Metrics */}
      <div>
        <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Target className="w-5 h-5 text-blue-600" />
          Today: Current Capacity Status
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <MetricCard
            title="Total Capacity"
            value={formatHours(summary?.total_capacity_hours)}
            subtitle="Portfolio-wide capacity"
            icon={Gauge}
            color="blue"
            testId="metric-total-capacity"
          />
          <MetricCard
            title="Allocated"
            value={formatPercent(summary?.overall_allocated_percent)}
            subtitle={formatHours(summary?.total_allocated_hours)}
            icon={Activity}
            color={summary?.overall_allocated_percent > 100 ? 'red' : summary?.overall_allocated_percent > 80 ? 'amber' : 'green'}
            testId="metric-allocated"
          />
          <MetricCard
            title="Available"
            value={formatHours(summary?.total_available_hours)}
            subtitle="Remaining capacity"
            icon={CheckCircle}
            color="green"
            testId="metric-available"
          />
          <MetricCard
            title="Overallocated"
            value={summary?.resources_overallocated || 0}
            subtitle="Resources > 100%"
            icon={AlertTriangle}
            color={summary?.resources_overallocated > 0 ? 'red' : 'green'}
            testId="metric-overallocated"
          />
          <MetricCard
            title="Critical At Risk"
            value={summary?.critical_resources_at_risk || 0}
            subtitle="Mission-critical resources"
            icon={Shield}
            color={summary?.critical_resources_at_risk > 0 ? 'red' : 'green'}
            testId="metric-critical-at-risk"
          />
        </div>
      </div>
      
      {/* Bottlenecks Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Current Bottlenecks */}
        <div className="bg-white rounded-xl border p-6">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            Where Are Bottlenecks?
          </h3>
          
          {(!dashboard?.top_overallocations || dashboard.top_overallocations.length === 0) ? (
            <div className="text-center py-8 text-slate-400">
              <CheckCircle className="w-10 h-10 mx-auto mb-2 text-emerald-400" />
              <p>No capacity bottlenecks detected</p>
            </div>
          ) : (
            <div className="space-y-3">
              {dashboard.top_overallocations.slice(0, 5).map((resource) => (
                <div 
                  key={resource.resource_id}
                  className="p-4 rounded-lg bg-red-50 border border-red-100 hover:shadow-md cursor-pointer transition-all"
                  onClick={() => onViewResource?.(resource)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="font-medium text-slate-900">{resource.name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <ResourceTypeBadge type={resource.resource_type} />
                        <CriticalityBadge criticality={resource.criticality} />
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-red-600">
                        {formatPercent(resource.allocated_percent)}
                      </p>
                      <p className="text-xs text-slate-500">
                        +{formatHours(resource.overallocation_hours)} over
                      </p>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600">
                    {resource.allocations?.length || 0} project allocations • {resource.team_name || 'No team'}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Critical Dependencies */}
        <div className="bg-white rounded-xl border p-6">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-orange-500" />
            Critical Dependencies
          </h3>
          
          {(!dashboard?.top_critical_dependencies || dashboard.top_critical_dependencies.length === 0) ? (
            <div className="text-center py-8 text-slate-400">
              <CheckCircle className="w-10 h-10 mx-auto mb-2 text-emerald-400" />
              <p>No critical dependencies at risk</p>
            </div>
          ) : (
            <div className="space-y-3">
              {dashboard.top_critical_dependencies.slice(0, 5).map((resource) => (
                <div 
                  key={resource.resource_id}
                  className="p-4 rounded-lg bg-orange-50 border border-orange-100 hover:shadow-md cursor-pointer transition-all"
                  onClick={() => onViewResource?.(resource)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="font-medium text-slate-900">{resource.name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <CriticalityBadge criticality={resource.criticality} />
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          resource.risk_level === 'high' ? 'bg-red-100 text-red-700' :
                          resource.risk_level === 'medium' ? 'bg-amber-100 text-amber-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>
                          {resource.risk_level?.toUpperCase()} Risk
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-semibold text-slate-700">
                        {resource.projects_dependent} projects
                      </p>
                      <p className="text-xs text-slate-500">
                        depend on this resource
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {resource.skills?.slice(0, 4).map((skill, idx) => (
                      <span key={idx} className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* Capacity Heatmap */}
      <div className="bg-white rounded-xl border p-6">
        <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-purple-500" />
          Capacity Heatmap by Team
        </h3>
        <CapacityHeatmap data={heatmap} groupBy="team" />
      </div>
      
      {/* Resource Type Distribution */}
      {typeData.length > 0 && (
        <div className="bg-white rounded-xl border p-6">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <PieChartIcon className="w-5 h-5 text-indigo-500" />
            Resource Distribution by Type
          </h3>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={typeData}
                cx="50%"
                cy="50%"
                outerRadius={100}
                dataKey="value"
                label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                labelLine={true}
              >
                {typeData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => `${value} resources`} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}
      
      {/* Next Quarter: Capacity Forecast */}
      {forecast && (
        <div className="bg-white rounded-xl border p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-purple-600" />
            Next Quarter: Capacity Forecast
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="p-4 rounded-lg bg-slate-50 border">
              <p className="text-sm text-slate-500 mb-1">Forecast Horizon</p>
              <p className="text-xl font-bold text-slate-900">{forecast.forecast_horizon_weeks || 12} weeks</p>
              <p className="text-xs text-slate-400">Through {forecast.forecast_end_date ? new Date(forecast.forecast_end_date).toLocaleDateString() : '-'}</p>
            </div>
            <div className="p-4 rounded-lg bg-slate-50 border">
              <p className="text-sm text-slate-500 mb-1">Projected Allocation</p>
              <p className="text-xl font-bold text-slate-900">
                {formatPercent(forecast.forecast_capacity?.projected_allocated_percent)}
              </p>
              <p className="text-xs text-slate-400">
                {forecast.forecast_capacity?.trend === 'improving' ? '↓ Improving' : '→ Stable'}
              </p>
            </div>
            <div className="p-4 rounded-lg bg-slate-50 border">
              <p className="text-sm text-slate-500 mb-1">Capacity Freed</p>
              <p className="text-xl font-bold text-emerald-600">
                {formatHours(forecast.forecast_capacity?.capacity_freed_hours)}
              </p>
              <p className="text-xs text-slate-400">Allocations ending</p>
            </div>
            <div className={`p-4 rounded-lg border ${
              forecast.capacity_risk?.level === 'CRITICAL' ? 'bg-red-50 border-red-200' :
              forecast.capacity_risk?.level === 'HIGH' ? 'bg-orange-50 border-orange-200' :
              forecast.capacity_risk?.level === 'MEDIUM' ? 'bg-amber-50 border-amber-200' :
              'bg-emerald-50 border-emerald-200'
            }`}>
              <p className="text-sm text-slate-500 mb-1">Risk Level</p>
              <p className={`text-xl font-bold ${
                forecast.capacity_risk?.level === 'CRITICAL' ? 'text-red-600' :
                forecast.capacity_risk?.level === 'HIGH' ? 'text-orange-600' :
                forecast.capacity_risk?.level === 'MEDIUM' ? 'text-amber-600' :
                'text-emerald-600'
              }`}>
                {forecast.capacity_risk?.level || 'LOW'}
              </p>
              <p className="text-xs text-slate-500">Score: {forecast.capacity_risk?.score || 0}/100</p>
            </div>
          </div>
          
          {/* Constraints & Recommendations */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Emerging Constraints */}
            <div>
              <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                Emerging Constraints
              </h4>
              {(!forecast.constraint_forecast || forecast.constraint_forecast.length === 0) ? (
                <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-100 text-center">
                  <CheckCircle className="w-6 h-6 text-emerald-500 mx-auto mb-2" />
                  <p className="text-sm text-emerald-700">No emerging constraints</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {forecast.constraint_forecast.map((c, idx) => (
                    <div key={idx} className={`p-3 rounded-lg border ${
                      c.severity === 'critical' ? 'bg-red-50 border-red-200' :
                      c.severity === 'high' ? 'bg-orange-50 border-orange-200' :
                      'bg-amber-50 border-amber-200'
                    }`}>
                      <p className="font-medium text-slate-900 text-sm">{c.description}</p>
                      <p className="text-xs text-slate-500 mt-1">{c.mitigation}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Recommendations */}
            <div>
              <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                <Lightbulb className="w-4 h-4 text-blue-500" />
                Recommendations
              </h4>
              {(!forecast.recommendations || forecast.recommendations.length === 0) ? (
                <div className="p-4 rounded-lg bg-slate-50 border text-center">
                  <p className="text-sm text-slate-500">No recommendations at this time</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {forecast.recommendations.map((rec, idx) => (
                    <div key={idx} className="p-3 rounded-lg bg-blue-50 border border-blue-100">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          rec.priority === 'high' ? 'bg-red-100 text-red-700' :
                          rec.priority === 'medium' ? 'bg-amber-100 text-amber-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>
                          {rec.priority?.toUpperCase()}
                        </span>
                        <p className="font-medium text-slate-900 text-sm">{rec.action}</p>
                      </div>
                      <p className="text-xs text-slate-600">{rec.description}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Future Agentic PM Recommendation Preview */}
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-100 p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-indigo-100 rounded-xl">
            <Brain className="w-6 h-6 text-indigo-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-slate-900 mb-2">
              Future: Agentic PM Recommendations
            </h3>
            <p className="text-sm text-slate-600 mb-4">
              When Resource Intelligence is complete, Agentic PM Level 3 will consume this data to provide 
              intelligent resource recommendations like:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start gap-2 p-3 bg-white rounded-lg border">
                <Lightbulb className="w-4 h-4 text-amber-500 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-slate-700">Delay Project X</p>
                  <p className="text-xs text-slate-500">Capacity shortfall detected</p>
                </div>
              </div>
              <div className="flex items-start gap-2 p-3 bg-white rounded-lg border">
                <Lightbulb className="w-4 h-4 text-blue-500 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-slate-700">Reallocate Team Y</p>
                  <p className="text-xs text-slate-500">Underutilized capacity available</p>
                </div>
              </div>
              <div className="flex items-start gap-2 p-3 bg-white rounded-lg border">
                <Lightbulb className="w-4 h-4 text-purple-500 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-slate-700">Add Contractor Capacity</p>
                  <p className="text-xs text-slate-500">Skill gap identified</p>
                </div>
              </div>
              <div className="flex items-start gap-2 p-3 bg-white rounded-lg border">
                <Lightbulb className="w-4 h-4 text-red-500 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-slate-700">Escalate Capacity Risk</p>
                  <p className="text-xs text-slate-500">Mission-critical resource constrained</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// PMO VIEW - Operational capacity management
// =============================================================================

const PMOView = ({ dashboard, heatmap, underutilized, onViewResource }) => {
  const [heatmapGroupBy, setHeatmapGroupBy] = useState('team');
  const { summary } = dashboard || {};
  
  return (
    <div className="space-y-6" data-testid="pmo-view">
      {/* PMO Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Resources"
          value={summary?.total_resources || 0}
          subtitle="Active capacity assets"
          icon={Users}
          color="blue"
          testId="metric-total-resources"
        />
        <MetricCard
          title="Demand vs Capacity"
          value={formatPercent(summary?.overall_allocated_percent)}
          subtitle={`${formatHours(summary?.total_allocated_hours)} / ${formatHours(summary?.total_capacity_hours)}`}
          icon={Activity}
          color={summary?.overall_allocated_percent > 100 ? 'red' : summary?.overall_allocated_percent > 80 ? 'amber' : 'green'}
          testId="metric-demand-capacity"
        />
        <MetricCard
          title="Near Capacity"
          value={summary?.resources_near_capacity || 0}
          subtitle="80-100% allocated"
          icon={AlertCircle}
          color={summary?.resources_near_capacity > 5 ? 'amber' : 'green'}
          testId="metric-near-capacity"
        />
        <MetricCard
          title="Utilization"
          value={formatPercent(summary?.overall_utilized_percent)}
          subtitle="Actual work performed"
          icon={Zap}
          color={summary?.overall_utilized_percent > 85 ? 'green' : summary?.overall_utilized_percent < 50 ? 'amber' : 'green'}
          testId="metric-utilization"
        />
      </div>
      
      {/* Capacity Heatmap with Toggle */}
      <div className="bg-white rounded-xl border p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-purple-500" />
            Capacity Heatmap
          </h3>
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500">Group by:</span>
            <select
              value={heatmapGroupBy}
              onChange={(e) => setHeatmapGroupBy(e.target.value)}
              className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm"
            >
              <option value="team">Team</option>
              <option value="type">Resource Type</option>
            </select>
          </div>
        </div>
        
        {/* Heatmap Legend */}
        <div className="flex items-center gap-4 mb-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-emerald-500" />
            <span className="text-slate-600">Available (0-80%)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-amber-500" />
            <span className="text-slate-600">Near Capacity (80-100%)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-red-500" />
            <span className="text-slate-600">Overallocated (100%+)</span>
          </div>
        </div>
        
        <CapacityHeatmap data={heatmap} groupBy={heatmapGroupBy} />
      </div>
      
      {/* Team Summary Table */}
      <div className="bg-white rounded-xl border p-6">
        <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Layers className="w-5 h-5 text-indigo-500" />
          Team Capacity Summary
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Team</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Resources</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Capacity</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Allocated</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Available</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">Status</th>
              </tr>
            </thead>
            <tbody>
              {(!heatmap || heatmap.length === 0) ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-slate-400">
                    No team data available
                  </td>
                </tr>
              ) : (
                heatmap.map((team, idx) => (
                  <tr key={team.id || idx} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="py-3 px-4 font-medium text-slate-900">{team.name || 'Unassigned'}</td>
                    <td className="text-right py-3 px-4 text-slate-700">{team.resource_count}</td>
                    <td className="text-right py-3 px-4 text-slate-700">{formatHours(team.total_capacity_hours)}</td>
                    <td className="text-right py-3 px-4 text-slate-700">
                      {formatHours(team.total_allocated_hours)}
                      <span className="text-slate-400 ml-1">({formatPercent(team.allocated_percent)})</span>
                    </td>
                    <td className="text-right py-3 px-4 text-slate-700">{formatHours(team.available_hours)}</td>
                    <td className="py-3 px-4 text-center">
                      <AllocationStatusBadge status={team.status} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Underutilized Resources */}
      <div className="bg-white rounded-xl border p-6">
        <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <UserX className="w-5 h-5 text-amber-500" />
          Underutilized Resources
          <span className="text-sm font-normal text-slate-500 ml-2">
            ({underutilized?.length || 0} resources below 50% utilization)
          </span>
        </h3>
        
        {(!underutilized || underutilized.length === 0) ? (
          <div className="text-center py-8 text-slate-400">
            <CheckCircle className="w-10 h-10 mx-auto mb-2 text-emerald-400" />
            <p>All resources are optimally utilized</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {underutilized.slice(0, 9).map((resource) => (
              <div 
                key={resource.resource_id}
                className="p-4 rounded-lg bg-amber-50 border border-amber-100 hover:shadow-md cursor-pointer transition-all"
                onClick={() => onViewResource?.(resource)}
              >
                <div className="flex items-center justify-between mb-2">
                  <p className="font-medium text-slate-900 truncate">{resource.name}</p>
                  <span className="text-lg font-bold text-amber-600">
                    {formatPercent(resource.utilized_percent)}
                  </span>
                </div>
                <p className="text-sm text-slate-600">
                  {resource.team_name || 'No team'} • {formatHours(resource.available_hours)} available
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// RESOURCE MANAGER VIEW - Individual resource health
// =============================================================================

const ResourceManagerView = ({ dashboard, resources, skillGaps, onViewResource }) => {
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('allocation');
  
  // Filter resources
  const filteredResources = (resources || []).filter(r => {
    if (filter === 'all') return true;
    if (filter === 'overallocated') return r.allocation_status === 'overallocated';
    if (filter === 'near_capacity') return r.allocation_status === 'near_capacity';
    if (filter === 'available') return r.allocation_status === 'available';
    if (filter === 'mission_critical') return r.criticality === 'mission_critical';
    if (filter === 'underutilized') return r.utilization_level === 'underutilized';
    return true;
  });
  
  // Sort resources
  const sortedResources = [...filteredResources].sort((a, b) => {
    if (sortBy === 'allocation') return (b.current_allocated_percent || 0) - (a.current_allocated_percent || 0);
    if (sortBy === 'utilization') return (b.current_utilized_percent || 0) - (a.current_utilized_percent || 0);
    if (sortBy === 'capacity') return (b.capacity_hours_per_week || 0) - (a.capacity_hours_per_week || 0);
    if (sortBy === 'name') return (a.name || '').localeCompare(b.name || '');
    return 0;
  });
  
  // Counts
  const overallocatedCount = resources?.filter(r => r.allocation_status === 'overallocated').length || 0;
  const nearCapacityCount = resources?.filter(r => r.allocation_status === 'near_capacity').length || 0;
  const missionCriticalCount = resources?.filter(r => r.criticality === 'mission_critical').length || 0;
  const underutilizedCount = resources?.filter(r => r.utilization_level === 'underutilized').length || 0;
  
  return (
    <div className="space-y-6" data-testid="resource-manager-view">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Overallocated"
          value={overallocatedCount}
          subtitle="Capacity exceeded"
          icon={AlertTriangle}
          color={overallocatedCount > 0 ? 'red' : 'green'}
          onClick={() => setFilter('overallocated')}
          testId="metric-rm-overallocated"
        />
        <MetricCard
          title="Near Capacity"
          value={nearCapacityCount}
          subtitle="80-100% allocated"
          icon={AlertCircle}
          color={nearCapacityCount > 5 ? 'amber' : 'green'}
          onClick={() => setFilter('near_capacity')}
          testId="metric-rm-near-capacity"
        />
        <MetricCard
          title="Mission Critical"
          value={missionCriticalCount}
          subtitle="Key resources"
          icon={Shield}
          color="purple"
          onClick={() => setFilter('mission_critical')}
          testId="metric-rm-mission-critical"
        />
        <MetricCard
          title="Underutilized"
          value={underutilizedCount}
          subtitle="< 50% utilized"
          icon={TrendingDown}
          color={underutilizedCount > 3 ? 'amber' : 'green'}
          onClick={() => setFilter('underutilized')}
          testId="metric-rm-underutilized"
        />
      </div>
      
      {/* Skill Gaps */}
      {skillGaps && skillGaps.critical_gaps?.length > 0 && (
        <div className="bg-white rounded-xl border border-red-200 p-6">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            Skill Gap Alert
          </h3>
          <div className="flex flex-wrap gap-2">
            {skillGaps.critical_gaps.map((skill, idx) => {
              const skillData = skillGaps.skill_availability?.[skill] || {};
              return (
                <div 
                  key={idx}
                  className={`px-3 py-2 rounded-lg border ${
                    skillData.risk_level === 'high' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'
                  }`}
                >
                  <p className="font-medium text-slate-900">{skill}</p>
                  <p className="text-xs text-slate-500">
                    {skillData.total_resources || 0} resources • {skillData.available_resources || 0} available
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Filters and Sort */}
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm text-slate-500">Filter:</span>
        {[
          { key: 'all', label: 'All', count: resources?.length },
          { key: 'overallocated', label: 'Overallocated', count: overallocatedCount },
          { key: 'near_capacity', label: 'Near Capacity', count: nearCapacityCount },
          { key: 'available', label: 'Available', count: resources?.filter(r => r.allocation_status === 'available').length },
          { key: 'mission_critical', label: 'Mission Critical', count: missionCriticalCount }
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              filter === f.key 
                ? 'bg-slate-900 text-white' 
                : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            {f.label} ({f.count || 0})
          </button>
        ))}
        
        <div className="ml-auto flex items-center gap-2">
          <span className="text-sm text-slate-500">Sort:</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm"
          >
            <option value="allocation">Allocation (Highest)</option>
            <option value="utilization">Utilization (Highest)</option>
            <option value="capacity">Capacity (Highest)</option>
            <option value="name">Name (A-Z)</option>
          </select>
        </div>
      </div>
      
      {/* Resource Table */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Resource</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Type</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Team</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Capacity</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">Allocated</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">Utilized</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">Criticality</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">Status</th>
              </tr>
            </thead>
            <tbody>
              {sortedResources.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-slate-400">
                    <Users className="w-10 h-10 mx-auto mb-2" />
                    <p>No resources match the filter</p>
                  </td>
                </tr>
              ) : (
                sortedResources.slice(0, 50).map((resource) => (
                  <tr 
                    key={resource.resource_id || resource.id}
                    className={`border-b border-slate-50 hover:bg-slate-50 cursor-pointer ${
                      resource.allocation_status === 'overallocated' ? 'bg-red-50/30' : ''
                    }`}
                    onClick={() => onViewResource?.(resource)}
                  >
                    <td className="py-3 px-4">
                      <p className="font-medium text-slate-900">{resource.name}</p>
                      <p className="text-xs text-slate-500">{resource.role || resource.title || '-'}</p>
                    </td>
                    <td className="py-3 px-4">
                      <ResourceTypeBadge type={resource.resource_type} />
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-600">
                      {resource.team_name || 'Unassigned'}
                    </td>
                    <td className="text-right py-3 px-4 text-slate-700">
                      {formatHours(resource.capacity_hours_per_week)}/wk
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex justify-center">
                        <CapacityGauge value={resource.current_allocated_percent} size="small" />
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex justify-center">
                        <span className={`text-sm font-medium ${
                          resource.current_utilized_percent > 85 ? 'text-emerald-600' :
                          resource.current_utilized_percent < 50 ? 'text-amber-600' :
                          'text-slate-600'
                        }`}>
                          {formatPercent(resource.current_utilized_percent)}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <CriticalityBadge criticality={resource.criticality} />
                    </td>
                    <td className="py-3 px-4 text-center">
                      <AllocationStatusBadge status={resource.allocation_status} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// MAIN DASHBOARD COMPONENT
// =============================================================================

const ResourceDashboard = () => {
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState('executive');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Data state
  const [dashboard, setDashboard] = useState(null);
  const [heatmap, setHeatmap] = useState([]);
  const [bottlenecks, setBottlenecks] = useState({});
  const [underutilized, setUnderutilized] = useState([]);
  const [resources, setResources] = useState([]);
  const [skillGaps, setSkillGaps] = useState(null);
  const [forecast, setForecast] = useState(null);
  
  // Fetch all data
  const fetchData = useCallback(async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    else setLoading(true);
    
    try {
      const headers = getAuthHeaders();
      
      // Fetch all data in parallel
      const [dashboardRes, heatmapRes, bottlenecksRes, underutilizedRes, resourcesRes] = await Promise.all([
        axios.get(`${API_BASE}/resources/dashboard/executive`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API_BASE}/resources/capacity/heatmap?group_by=team`, { headers }).catch(() => ({ data: { heatmap: [] } })),
        axios.get(`${API_BASE}/resources/capacity/bottlenecks`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API_BASE}/resources/capacity/underutilized`, { headers }).catch(() => ({ data: { underutilized_resources: [] } })),
        axios.get(`${API_BASE}/resources?page_size=100`, { headers }).catch(() => ({ data: { resources: [] } }))
      ]);
      
      setDashboard(dashboardRes.data?.dashboard || {});
      setHeatmap(heatmapRes.data?.heatmap || []);
      setBottlenecks(bottlenecksRes.data || {});
      setUnderutilized(underutilizedRes.data?.underutilized_resources || []);
      setResources(resourcesRes.data?.resources || []);
      
      // Fetch skill gaps with common skills
      try {
        const skillGapsRes = await axios.post(
          `${API_BASE}/resources/capacity/skill-gaps`,
          { required_skills: ['Python', 'React', 'Cloud', 'Architecture', 'Security', 'DevOps'] },
          { headers }
        );
        setSkillGaps(skillGapsRes.data?.skill_gaps || null);
      } catch {
        setSkillGaps(null);
      }
      
      // Fetch capacity forecast
      try {
        const forecastRes = await axios.get(`${API_BASE}/resources/capacity/forecast?weeks=12`, { headers });
        setForecast(forecastRes.data?.forecast || null);
      } catch {
        setForecast(null);
      }
      
    } catch (error) {
      console.error('Failed to fetch resource data:', error);
      toast.error('Failed to load resource intelligence data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  const handleRefresh = () => fetchData(true);
  
  const handleViewResource = (resource) => {
    toast.info(`Resource: ${resource.name || resource.resource_id}`);
    // Could navigate to resource detail or open modal
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-slate-400 mx-auto" />
          <p className="mt-4 text-slate-500">Loading Resource Intelligence...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-50" data-testid="resource-dashboard">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                  <Users className="w-6 h-6 text-white" />
                </div>
                Resource Intelligence
              </h1>
              <p className="text-slate-500 mt-1">
                Capacity Intelligence Layer • Do we have enough capacity to deliver?
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
                data-testid="refresh-btn"
              >
                <RefreshCw className={`w-5 h-5 text-slate-600 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={() => navigate('/resources/explorer')}
                className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors"
                data-testid="new-resource-btn"
              >
                <Plus className="w-4 h-4" />
                Add Resource
              </button>
            </div>
          </div>
          
          {/* View Tabs */}
          <div className="flex gap-3 mt-6">
            <ViewTab 
              active={activeView === 'executive'} 
              label="Executive View" 
              icon={Target}
              onClick={() => setActiveView('executive')}
              testId="tab-executive"
            />
            <ViewTab 
              active={activeView === 'pmo'} 
              label="PMO View" 
              icon={BarChart3}
              count={dashboard?.summary?.total_resources}
              onClick={() => setActiveView('pmo')}
              testId="tab-pmo"
            />
            <ViewTab 
              active={activeView === 'resource_manager'} 
              label="Resource Manager" 
              icon={Users}
              count={bottlenecks?.total_bottlenecks || 0}
              onClick={() => setActiveView('resource_manager')}
              testId="tab-resource-manager"
            />
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {activeView === 'executive' && (
          <ExecutiveView 
            dashboard={dashboard}
            heatmap={heatmap}
            bottlenecks={bottlenecks}
            forecast={forecast}
            onViewResource={handleViewResource}
          />
        )}
        {activeView === 'pmo' && (
          <PMOView 
            dashboard={dashboard}
            heatmap={heatmap}
            underutilized={underutilized}
            onViewResource={handleViewResource}
          />
        )}
        {activeView === 'resource_manager' && (
          <ResourceManagerView 
            dashboard={dashboard}
            resources={resources}
            skillGaps={skillGaps}
            onViewResource={handleViewResource}
          />
        )}
      </div>
    </div>
  );
};

export default ResourceDashboard;
